import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "../lib/api";
import { caughtErrorMessage } from "../domain/errors";
import {
  JURISDICTIONS,
  JURISDICTION_LABELS,
} from "../../convex/model/enums";
import {
  emptyLegislationCreateFormState,
  emptyLegislationCreateSubmissionState,
  legislationCreateFormView,
  legislationCreateSubmissionError,
  legislationCreateSubmitButtonLabel,
  nextLegislationCreateFormState,
  nextLegislationCreateSubmissionState,
  type LegislationCreateFormAction,
} from "../domain/legislation";

export function EditorHome() {
  const legislation = useQuery(api.legislation.list, {}) ?? [];
  const create = useMutation(api.legislation.create);
  const navigate = useNavigate();

  const [createDraft, setCreateDraft] = useState(() => emptyLegislationCreateFormState());
  const [submission, setSubmission] = useState(() => emptyLegislationCreateSubmissionState());
  const createView = useMemo(() => legislationCreateFormView(createDraft), [createDraft]);
  const submissionError = legislationCreateSubmissionError(submission);

  const updateCreateDraft = (action: LegislationCreateFormAction) => {
    setCreateDraft((current) => nextLegislationCreateFormState(current, action));
    setSubmission((current) => nextLegislationCreateSubmissionState(current, { type: "cleared" }));
  };

  const submit = async () => {
    if (!createView.prepared.ok || submission.kind === "creating") return;
    setSubmission((current) => nextLegislationCreateSubmissionState(current, { type: "started" }));
    try {
      const id = await create(createView.prepared.value);
      navigate(`/editor/${id}`);
    } catch (e) {
      setSubmission((current) => nextLegislationCreateSubmissionState(current, {
        type: "failed",
        error: caughtErrorMessage(e, "Failed to create"),
      }));
    }
  };

  return (
    <div className="mx-auto max-w-[1180px] space-y-6 px-6 py-7 lg:px-9">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-2xl font-semibold">Editor</h1>
        <Link to="/editor/diff-tool" className="rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
          Amendment diff tool
        </Link>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-3 font-semibold">New legislation</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <input aria-label="Legislation title" className="rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Title, e.g. Privacy Act 1988" value={createDraft.title} onChange={(e) => updateCreateDraft({ type: "titleInput", value: e.target.value })} />
          <input
            className={`rounded border px-3 py-2 text-sm ${createView.referenceInputClassName}`}
            aria-label="Legislation reference"
            placeholder="Reference, e.g. PRIVACYACT1988"
            value={createDraft.reference}
            onChange={(e) => updateCreateDraft({ type: "referenceInput", value: e.target.value })}
          />
          <select aria-label="Legislation jurisdiction" className="rounded border border-slate-300 px-3 py-2 text-sm" value={createDraft.jurisdiction} onChange={(e) => updateCreateDraft({ type: "jurisdictionInput", value: e.target.value })}>
            {JURISDICTIONS.map((j) => (
              <option key={j} value={j}>
                {JURISDICTION_LABELS[j]}
              </option>
            ))}
          </select>
          <select aria-label="Legislation type" className="rounded border border-slate-300 px-3 py-2 text-sm" value={createDraft.kind} onChange={(e) => updateCreateDraft({ type: "kindInput", value: e.target.value })}>
            <option value="act">Act</option>
            <option value="regulation">Regulation</option>
          </select>
        </div>
        {createView.problem !== null && <p className="mt-2 text-sm text-amber-700">{createView.problem}</p>}
        {submissionError !== null && <p className="mt-2 text-sm text-red-600">{submissionError}</p>}
        <button
          type="button"
          disabled={!createView.canSubmit || submission.kind === "creating"}
          onClick={submit}
          className="mt-3 rounded bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-40"
        >
          {legislationCreateSubmitButtonLabel(submission)}
        </button>
      </section>

      <section>
        <h2 className="mb-2 font-semibold">All legislation ({legislation.length})</h2>
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
          {legislation.map((l) => (
            <li key={l._id}>
              <Link to={`/editor/${l._id}`} className="flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50">
                <span>
                  {l.title}
                  <span className="ml-2 text-xs text-slate-400">{l.reference}</span>
                </span>
                <span className="text-xs text-slate-400">{JURISDICTION_LABELS[l.jurisdiction]} · {l.kind}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
